import {KycForm} from './../types/KycForm';
import {Turnover} from '~local/app/shared/types/kycTierLimit';
import {Injectable} from '@angular/core';
import {BehaviorSubject, Observable, shareReplay, take} from 'rxjs';
import {User, UserProfile} from '../types/User';
import {isEmpty} from '../Helper';
import {HttpClient} from '@angular/common/http';
import {BankAccount} from '../types/BankAccount';
import {Product} from '../types/product';
import {catchError, filter, tap} from 'rxjs/operators';
import {SocketNamespaceServiceBase} from '~local/app/core/socket/SocketNamespaceServiceBase';
import {Resolve} from '@angular/router';
import {AuthenticationService} from '~local/app/core/services/authentication.service';
import {MultipleAuthForm} from '~shared/types/authentication.types';
import {KycStatus, KycTier, KycType} from '~shared/types/kyc.types';

type KycTierResponse = {
    final: boolean,
    rejectionReason: string | null,
    status: KycStatus,
    tier: number,
    updatedDate: string,
};

type BankAccountResponse = Exclude<BankAccount, 'createdDate' | 'updatedDate'> & {
    createdDate: string,
    updatedDate: string,
};

export function bankAccountResponseToBankAccount($response: Partial<BankAccountResponse>): Partial<BankAccount> {
    return Object.keys($response).reduce(($acc, $responseKey) => {
        if ($responseKey === 'createdDate' || $responseKey === 'updatedDate') {
            const dateParsed = Date.parse($response[$responseKey]);
            return {
                ...$acc,
                [$responseKey]: isNaN(dateParsed) ? null : new Date(dateParsed),
            };
        }

        return {
            ...$acc,
            [$responseKey]: $response[$responseKey]
        };
    }, <Partial<BankAccount>>{});
}

export type UserResponse = Exclude<User, 'birthDate' | 'bankAccount'> & {
    hasCard: boolean,
    birthDate: string | null,
    bankAccount: BankAccountResponse | null,
};

export function userResponseToUser($response: Partial<UserResponse>): Partial<User> {
    const birthDateParsed = Date.parse($response.birthDate);
    return {
        ...$response,
        birthDate: isNaN(birthDateParsed) ? null : new Date(birthDateParsed),
        bankAccount: $response.bankAccount ? bankAccountResponseToBankAccount($response.bankAccount) : null,
    };
}

export type UserUpdateResponse = Pick<
    UserResponse,
    'twoFactorAuthEnabled' | 'kycVerificationStatus' | 'kycTier' | 'emailConfirmed' | 'recoveryEmail' | 'recoveryEmailConfirmed' | 'recoveryNumber' | 'recoveryNumberConfirmed'
    | 'hideValues' | 'detained' | 'detainedReason' | 'whitelistEnabled' | 'hasCard'
>;

export type ProfileResponse = Pick<UserProfile, 'address' | 'address2' | 'username' | 'birthDate' | 'city' | 'country' | 'firstName' | 'lastName' | 'postalCode' | 'state'>;

enum UserUpdateEvent {
    UPDATE = 'update',
    UPDATE_PROFILE = 'update-profile',
    UPDATE_BANK = 'update-bank',
    INSERT_KYC = 'insert-kyc',
    DELETE_KYC = 'delete-kyc',
    UPDATE_KYC = 'update-kyc',
    MAINTENANCE = 'maintenance',
}

@Injectable()
export class UserService extends SocketNamespaceServiceBase implements Resolve<User> {

    private _userData$ = new BehaviorSubject<User | null>(null);

    public showBalance: boolean = false;
    // TODO fix api logic related to this
    public maintenanceMode: { mode: boolean, reason: string } = {mode: false, reason: null};
    public kycForm: KycForm | null = null;

    constructor(
        protected readonly authenticationService: AuthenticationService,
        protected readonly httpClient: HttpClient,
    ) {

        super('user');

        this.onUpdate = this.onUpdate.bind(this);
        this.onUpdateBank = this.onUpdateBank.bind(this);
        this.onKycUpdate = this.onKycUpdate.bind(this);
        this.onKycInsert = this.onKycInsert.bind(this);
        this.onKycFinalReject = this.onKycFinalReject.bind(this);
        this.setMaintenanceMode = this.setMaintenanceMode.bind(this);

        this.authenticationService.credentials$.pipe(
            filter($ => $ === null),
        ).subscribe(() => {
            if (this._userData$ !== null) {
                this._userData$.next(null);
            }
        });
    }

    public uploadAvatar($avatar: Blob): Observable<Pick<UserProfile, 'avatar'>> {
        const formData = new FormData();
        formData.append('avatar', $avatar);

        return this.httpClient.put<Pick<UserProfile, 'avatar'>>('/profile/me', formData).pipe(
            tap($ => {
                this._userData$.next({
                    ...this._userData$.value,
                    ...$,
                });
            }),
        );
    }

    public enableWhitelist($data: MultipleAuthForm & {enabled: boolean}): Observable<Pick<UserResponse, 'whitelistEnabled'>> {
        return this.httpClient.post<Pick<UserResponse, 'whitelistEnabled'>>('/users/me/whitelist/enabled', $data);
    }

    // FIXME: Drop this from required abstract override
    protected initListeners() {
        this.socket.on(UserUpdateEvent.UPDATE, this.onUpdate.bind(this));
        this.socket.on(UserUpdateEvent.UPDATE_PROFILE, this.onUpdateProfile.bind(this));
        this.socket.on(UserUpdateEvent.UPDATE_BANK, this.onUpdateBank.bind(this));
        this.socket.on(UserUpdateEvent.UPDATE_KYC, this.onKycUpdate.bind(this));
        this.socket.on(UserUpdateEvent.INSERT_KYC, this.onKycInsert.bind(this));
        this.socket.on(UserUpdateEvent.DELETE_KYC, this.onKycFinalReject.bind(this));
        // FIXME: This is not a place for that!!!
        this.socket.on(UserUpdateEvent.MAINTENANCE, this.setMaintenanceMode.bind(this));
    }

    // Getters
    public get<Key extends keyof User>(key: Key): User[Key] | null {
        if (this._userData$.value && !isEmpty(this._userData$.value[key])) {
            return this._userData$.value[key];
        }

        return (this._userData$.value && this._userData$.value[key]) || null;
    }

    public resolve(): Observable<User> {
        return this._userData$.pipe(
            filter($ => $ !== null),
            take(1),
        );
    }

    protected onConnect() {
        this.socket.once('dataR', ($data: UserResponse) => {

            this._userData$.next({
                ...this._userData$.value,
                ...userResponseToUser($data),
            });

            this.eventSubject.next({name: 'data_loaded', args: [this.data]});
        });
        this.socket.emit('data');
    }

    public onUpdate($data: Partial<UserUpdateResponse>) {
        this._userData$.next({
            ...this._userData$.value,
            ...$data,
        });

        // FIXME: FIX this!
        // this.data.avatar = '/assets/images/user-blank.jpg';
        this.eventSubject.next({name: 'update'});
    }

    public onUpdateProfile($data: Partial<ProfileResponse>) {
        this._userData$.next({
            ...this._userData$.value,
            ...$data,
        });
        this.eventSubject.next({name: 'update'});
    }

    private onUpdateBank($data: BankAccountResponse | null) {
        // Actually this is a delete
        if ($data === null) {
            this._userData$.next({
                ...this._userData$.value,
                bankAccount: null,
            });
        } else {
            this._userData$.next({
                ...this._userData$.value,
                bankAccount: {
                    ...this._userData$.value.bankAccount,
                    ...bankAccountResponseToBankAccount($data),
                },
            });
        }
        this.eventSubject.next({name: 'update-bank'});
    }

    // public onUpdated(cb: () => void): Subscription {
    //     return this.onEvent('update', cb);
    // }
    //
    // public onBankUpdated(cb: () => void): Subscription {
    //     return this.onEvent('update-bank', cb);
    // }

    private onKycUpdate($data: KycTierResponse) {
        const existing = this.kycRecordByTier($data.tier);
        if (existing) {
            this._userData$.next({
                ...this._userData$.value,
                kyc: this._userData$.value.kyc.map($ => {
                    if ($.tier === $data.tier) {
                        return {
                            ...$,
                            ...$data,
                        };
                    }
                    return $;
                })
            });
            this.eventSubject.next({name: 'update-kyc'});
        }
    }

    // TODO: Not sure about type!
    private onKycInsert(kycTier: KycTier) {
        const existing = this.kycRecordByTier(kycTier.tier);
        if (!existing) {
            this._userData$.next({
                ...this._userData$.value,
                kyc: [...this._userData$.value.kyc, kycTier],
            });
        }

        this.eventSubject.next({name: 'insert-kyc'});
    }

    public getKycTier() {
        // return this.get('kycTier');
        return 4;
    }

    public kycRecordByTier(tier: number): KycTier | null {
        if (this.data?.kyc?.length > 0) {
            return this.data.kyc.find(r => r.tier === tier);
        }
        return null;
    }

    public kycRecordByType($type: KycType): Array<KycTier> | null {
        if (this.data && Array.isArray(this.data.kyc)) {
            const result = this.data.kyc.filter($ => $.type === $type);
            if (result.length > 0) {
                return result;
            }
        }
        return null;
    }

    public getKycStatus() {
        return this.data ? this.data.kycVerificationStatus : null;
    }

    public setShowBalance(showBalance: boolean) {
        this.showBalance = showBalance;
        // localStorage.setItem('showBalance', this.showBalance ? 'true' : 'false');
    }

    public getDefaultShowBalance() {
        return !!this.data && this.data.hideValues;
    }

    public getWithdrawWhitelistEnabled(): boolean {
        return !!this.data && this.data.whitelistEnabled;
    }

    public setDefaultShowBalance() {
        this.showBalance = !Boolean(this.data.hideValues);

        // if (localStorage.getItem('showBalance')) {
        //     localStorage.removeItem('showBalance');
        // }
    }

    setMaintenanceMode(data: { mode: boolean, reason: string }) {
        if (!data.mode && !!data.mode) {
            return;
        }

        this.maintenanceMode = data;
    }

    public getUserTurnover(): Observable<Array<Turnover>> {
        return this.httpClient.get<Array<Turnover>>('/users/me/turnover').pipe(
            catchError(($) => {
                console.error($);
                return [];
            })
        );
    }

    public showKycTierForm(tierNr: number, prevTier: number, kycPayment: Product): boolean {
        if (!kycPayment) {
            return false;
        }

        const kycTier: KycTier = this.kycRecordByTier(tierNr);

        if (kycTier == null && this.data.kycTier !== prevTier) {
            return true;
        } else if (kycTier == null && !!kycPayment.disabled) {
            return true;
        } else if (kycTier != null && kycTier.status === 'PAID') {
            return true;
        }

        return false;
    }

    private onKycFinalReject(data: Partial<KycTier>) {
        const index = this.data.kyc.findIndex(k => k.tier === data.tier);

        if (index > -1) {
            this.data.kyc.splice(index, 1);
        }
    }

    public get data(): User | null {
        return this._userData$.value;
    }

    public get userData$(): Observable<User | null> {
        return this._userData$.pipe(shareReplay(1));
    }
}
